import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';

// State hooks
import { usePosts, useFeeds, useHashtags, useLikes, useBookmarks } from '@/Recoil/Social';
import { useMrMate } from '@/Recoil/Social/MrMate';
import { useUsers } from '@/Recoil/User';
import { contentFilter } from '@/Data/Social/ContentFilter';

// Components
import SocialHeader from '@/Components/social/SocialHeader';
import FilterTabs from '@/Components/social/FilterTabs';
import FeedGroup from '@/Components/social/FeedGroup';
import NewPostButton from '@/Components/social/NewPostButton';
import LoadingView from '@/Components/social/LoadingView';

// Styles
import './index.scss';

// Types
import type { Post } from '@/Data/Social';
import type { EssayTopic } from 'ai-api/types/social';

// i18n
import { tLoader } from '@/Utils/i18n';
import { languageManager } from '@/Utils/languageManager';

const translations = {
    en: {
        "No posts yet": "No posts yet",
        "Be the first to share something!": "Be the first to share something!",
        "See more in MATE App": "See more in MATE App",
        "Loading more posts...": "Loading more posts..."
    },
    id: {
        "No posts yet": "Belum ada postingan",
        "Be the first to share something!": "Jadilah yang pertama berbagi sesuatu!",
        "See more in MATE App": "Lihat lebih banyak di Aplikasi MATE",
        "Loading more posts...": "Memuat lebih banyak postingan..."
    },
    ko: {
        "No posts yet": "아직 게시물이 없습니다",
        "Be the first to share something!": "첫 번째로 무언가를 공유해보세요!",
        "See more in MATE App": "MATE 앱에서 더 보기",
        "Loading more posts...": "게시물을 더 불러오는 중..."
    },
    vn: {
        "No posts yet": "Chưa có bài đăng nào",
        "Be the first to share something!": "Hãy là người đầu tiên chia sẻ điều gì đó!",
        "See more in MATE App": "Xem thêm trong Ứng dụng MATE",
        "Loading more posts...": "Đang tải thêm bài đăng..."
    }
};

// import useRaffle from '@/Recoil/Raffle';

const Social = () => {
    const queryParams = Object.fromEntries(new URLSearchParams(window.location.search).entries());
    const filter = queryParams.filter || undefined;
    const hashtag = queryParams.hashtag || undefined;

    // i18n
    const [, forceUpdate] = useState({});
    const t = tLoader(translations);

    useEffect(() => {
        const unsubscribe = languageManager.subscribe(() => forceUpdate({}));
        return unsubscribe;
    }, []);

    // State hooks
    const { posts, loading: postsLoading } = usePosts();
    const { homeFeedGroups, getHomeFeed, loadMoreHomeFeed, searchPosts, loading: feedsLoading } = useFeeds();
    const { topics: mrMateTopics, refreshTopics: refreshMrMateTopics } = useMrMate();
    const [loadingMore, setLoadingMore] = useState(false);
    const { getTrendingHashtags } = useHashtags();
    const { syncUserLikes } = useLikes();
    const { syncUserBookmarks } = useBookmarks();
    const { syncProfiles } = useUsers();
    // const { syncTickets } = useRaffle();

    // Initialize data on mount
    const hasInitialized = useRef(false);
    const init = useRef(async () => {
        if (hasInitialized.current) return;
        hasInitialized.current = true;

        try {
            await Promise.all([
                getHomeFeed(),
                getTrendingHashtags(),
                syncUserLikes(),
                syncUserBookmarks(),
                refreshMrMateTopics(),
                // syncTickets(),
            ]);
        } catch (error) {
            console.error('Failed to initialize social data:', error);
        }
    });

    useEffect(() => {
        init.current();
    }, []);

    // Store searchPosts in a ref to avoid dependency issues
    const searchPostsRef = useRef(searchPosts);
    searchPostsRef.current = searchPosts;

    // Handle hashtag filter changes
    useEffect(() => {
        if (!hashtag) return;
        searchPostsRef.current({ hashtag });
    }, [hashtag]);

    // Create grouped sections from API response and merge MrMate topics
    const groupedSections = useMemo(() => {
        if (filter === 'survey' || filter === 'raffle') { return []; }

        // Create initial groups from feed
        const groups = homeFeedGroups.map(([category, postIds]) => ({
            category,
            contents: postIds
                .map(id => posts[id])
                .filter((post): post is Post => !!post?.created_at &&
                    !contentFilter.isPostFiltered(post.postid) &&
                    !contentFilter.isUserFiltered(post.addressid) &&
                    !contentFilter.hasFilteredHashtags(post.hashtags))
        })).filter(section => section.contents.length > 0);

        // Convert MrMate topics to posts and merge into groups
        mrMateTopics.forEach((topic: EssayTopic) => {
            const mrMatePost: Post = {
                postid: topic.topicid,
                addressid: 'mr_mate',
                content: `${topic.encouraging_id} ${topic.hashtags.join(' ')}`,
                translated: topic.encouraging_kr,
                created_at: topic.created_at || Math.floor(Date.now() / 1000),
                like_count: 0,
                reply_count: 0,
                view_count: 0,
                bookmark_count: 0,
                visibility: 'public' as const,
                hashtags: topic.hashtags.map(tag => tag.replace('#', '')),
                mentions: [],
                media: []
            };

            // Try to find existing group with matching hashtag
            const categoryName = (topic.hashtags[0] || 'Topic').replace('#', '');
            const categoryTranslation = (topic.hashtags[1] || topic.hashtags[0] || 'Topic').replace('#', '');
            const existingGroup = groups.find(g => g.category[0] === categoryName || g.category[1] === categoryName);

            if (existingGroup) {
                // Add to existing group at the beginning
                existingGroup.contents.unshift(mrMatePost);
            } else {
                // Create new group for this topic
                groups.unshift({
                    category: [categoryName, categoryTranslation] as [string, string],
                    contents: [mrMatePost]
                });
            }
        });

        return groups;
    }, [homeFeedGroups, posts, mrMateTopics, filter]);

    // Sync user profiles for visible posts
    useEffect(() => {
        const addressIds = groupedSections
            .flatMap(section => section.contents)
            .map(post => post.addressid)
            .filter((id, index, arr) => arr.indexOf(id) === index); // unique

        if (addressIds.length > 0) { syncProfiles(addressIds); }
    }, [groupedSections, syncProfiles]);


    const handleLoadMore = useCallback(async () => {
        if (loadingMore || feedsLoading) return;

        setLoadingMore(true);
        try {
            await loadMoreHomeFeed();
        } catch (error) {
            console.error('Failed to load more posts:', error);
        } finally {
            setLoadingMore(false);
        }
    }, [loadingMore, feedsLoading, loadMoreHomeFeed]);

    const loading = postsLoading || feedsLoading;
    const hasItems = groupedSections.length > 0;

    // Scroll handling with debounce
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    const lastScrollTop = useRef(0);

    const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
        const element = event.currentTarget;
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;

        // Check if scrolling down
        const isScrollingDown = scrollTop > lastScrollTop.current;
        lastScrollTop.current = scrollTop;

        // Only trigger if scrolling down
        if (!isScrollingDown) return;

        // Clear previous timeout
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        // Debounce the scroll check
        scrollTimeoutRef.current = setTimeout(() => {
            // Increase threshold to 300px (less sensitive)
            const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
            const isNearBottom = distanceFromBottom <= 300;

            if (isNearBottom && !loadingMore && !feedsLoading && hasItems) {
                handleLoadMore();
            }
        }, 150); // 150ms debounce
    }, [loadingMore, feedsLoading, hasItems, handleLoadMore]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="SocialPage">
            <SocialHeader
                onSearch={() => console.log('Search clicked')}
                onNotifications={() => console.log('Notifications clicked')}
            />
            <FilterTabs filter={filter} hashtag={hashtag} />

            {loading && !hasItems ? (
                <LoadingView />
            ) : !hasItems ? (
                <div className="Content Empty">
                    <div className="EmptyContent">
                        <h3 className="Title">{t('No posts yet')}</h3>
                        <p className="Description">{t('Be the first to share something!')}</p>
                    </div>
                </div>
            ) : (
                <div
                    className="Content"
                    onScroll={handleScroll}
                >

                    <div className="PostSections">
                        {groupedSections.map((section, index) => (
                            <FeedGroup
                                key={`${section.category[0]}-${index}`}
                                category={section.category}
                                contents={section.contents}
                            />
                        ))}
                    </div>

                    <div className="SeeMoreButton">
                        <button className="Button" onClick={()=>window.open('https://koreanmate.onelink.me/Tkco/y94asact', '_blank')}>{t('See more in MATE App')}</button>
                    </div>

                    {loadingMore && (
                        <div className="LoadingMore">
                            <span className="LoadingText">{t('Loading more posts...')}</span>
                        </div>
                    )}
                </div>
            )}

            <NewPostButton hashtag={hashtag} />
        </div>
    );
};

export default Social;

// Page-specific translations
export const localization = {
    en: {
        "Social": "Social",
        "All Posts": "All Posts",
        "Raffle": "Raffle",
    },
    id: {
        "Social": "Sosial",
        "All Posts": "Semua Postingan",
        "Raffle": "Undian",
    },
    ko: {
        "Social": "소셜",
        "All Posts": "모든 게시물",
        "Raffle": "추첨",
    }
};
